/**
 * Page Context Manager
 * Maintains menu context (loyal/avalon/default) across navigation using localStorage
 * Dynamically swaps header content when saved context differs from current page
 */

class PageContextManager {
  constructor() {
    this.storageKey = 'pageContext';
    this.init();
  }

  getHeaderGroupElement() {
    const selectors = [
      'shopify-section[id*="header"]',
      '[id*="shopify-section-header"]',
      '.shopify-section[class*="header"]',
      'section[id*="shopify-section-header"]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
    }

    console.error('❌ No header element found with any selector');
    return null;
  }

  init() {
    if (window.Shopify?.designMode) {
      const savedContext = this.getContext();
      this.applyContext(savedContext || this.detectPageContext());
      return;
    }

    const currentContext = this.detectPageContext();
 //   console.log('🔍 Current page context:', currentContext);


    let savedContext = this.getContext();
  //  console.log('💾 Saved context from localStorage:', savedContext);

    // In Avalon-only store: reset any legacy 'jae' context from the multi-brand store
    if (savedContext === 'jae') {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem('selectedLogo');
      savedContext = null;
    }

    // If there's a mismatch and we're on the home/preview page, trigger logo click
    if (savedContext && savedContext !== currentContext) {
      const isPreviewOrHome = currentContext === 'default' || currentContext === 'jae';
      
      const isActuallyHomePage = window.location.pathname === '/' || window.location.pathname === '/index';
      
      if (isActuallyHomePage && (savedContext === 'avalon' || savedContext === 'loyal')) {
  
        setTimeout(() => {
          const logoSelector = document.querySelector('logo-selector');
          if (logoSelector) {
            const targetLogo = logoSelector.querySelector(`[data-logo="${savedContext}"]`);
            if (targetLogo) {
              targetLogo.click();  
              return;
            } else {
              console.warn('⚠️ Logo link not found for:', savedContext);
            }
          } else {
            console.warn('⚠️ logo-selector element not found');
          }
          
          this.swapHeader(savedContext);
        }, 200);
        
        return; 
      }


      setTimeout(() => {
        this.swapHeader(savedContext);
      }, 100);
    } else {

      
      if (!savedContext && currentContext !== 'default') {
        this.setContext(currentContext);
      }
    }

    this.applyContext(savedContext || currentContext);
    
    // Cache the current page's menu for future use
    this.cacheCurrentMenu();
  }

  /**
   * Detect page context from template name or page handle
   */
  detectPageContext() {
    const bodyClasses = document.body.className;

    if (bodyClasses.includes('page-loyal') || bodyClasses.includes('loyal')) {
      return 'loyal';
    }
    if (bodyClasses.includes('page-avalon') || bodyClasses.includes('avalon')) {
      return 'avalon';
    }

    if (window.location.pathname === '/') return 'avalon';

    const path = window.location.pathname.toLowerCase();
    if (path.includes('/loyal')) return 'loyal';
    if (path.includes('/avalon')) return 'avalon';

    return 'default';
  }

  /**
   * Get saved context from localStorage
   */
  getContext() {
    return localStorage.getItem(this.storageKey);
  }

  /**
   * Save context to localStorage
   */
  setContext(context) {
    localStorage.setItem(this.storageKey, context);
    this.applyContext(context);
  }

  /**
   * Apply context to body classes for styling
   */
  applyContext(context) {
    document.body.classList.remove('jae', 'loyal', 'avalon');

    if (context === 'loyal') {
      document.body.classList.add('loyal');
    } else if (context === 'avalon') {
      document.body.classList.add('avalon');
    } else {
      document.body.classList.add('jae');
    }

    document.dispatchEvent(new CustomEvent('page-context-changed', {
      detail: { context }
    }));
  }

  /**
   * Swap header content by fetching the appropriate header-group section
   */
  async swapHeader(targetContext) {
    try {
      let fetchPage;

      if (targetContext === 'loyal') {
        fetchPage = '/pages/loyal';
      } else if (targetContext === 'avalon') {
        fetchPage = '/pages/avalon';
      } else {
        fetchPage = '/';
      } 

      const response = await fetch(fetchPage);

      if (!response.ok) {
        console.error('❌ Fetch failed:', response.status);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      /**
       * 1. Replace desktop nav
       */
      const newNav = doc.querySelector('.header__primary-nav');
      if (newNav) {
        const currentNav = document.querySelector('.header__primary-nav');
        if (currentNav) {
          currentNav.parentNode.replaceChild(newNav.cloneNode(true), currentNav);
        }
      }

      /**
       * 2. Replace mobile sidebar menu
       */
      const newSidebar = doc.querySelector('#sidebar-menu');
      if (newSidebar) {
        const currentSidebar = document.querySelector('#sidebar-menu');
        if (currentSidebar) {
          currentSidebar.parentNode.replaceChild(newSidebar.cloneNode(true), currentSidebar);
        }
      }

      this.reinitializeHeaderScripts();
    } catch (error) {
      console.error('❌ Error swapping menu:', error);
      console.error('Stack trace:', error.stack);
    }
  }

  /**
   * Re-initialize header scripts after swapping
   */
  reinitializeHeaderScripts() {


    document.dispatchEvent(new CustomEvent('header-swapped'));
  }


  /**
   * Cache the current page's nav ONLY if we are on the specific base page for each context
   */
  cacheCurrentMenu() {
    const path = window.location.pathname.toLowerCase();
    let targetContext = null;

    // Strict check: Only cache if we are on the "Source of Truth" pages
    if (path === '/' || path === '/index') {
      targetContext = 'avalon';
    } else if (path.includes('/pages/avalon')) {
      targetContext = 'avalon';
    } else if (path.includes('/pages/loyal')) {
      targetContext = 'loyal';
    }

    if (!targetContext) {
      console.log('🚫 Not a base page, skipping menu cache update.');
      return;
    }

    /**
     * Cache the complete nav element with all mega menu blocks
     */
    const navEl = document.querySelector('.header__primary-nav');

    if (navEl) {
      const cacheKey = `menu_cache_${targetContext}`;
      try {
        localStorage.setItem(cacheKey, navEl.outerHTML);
      } catch (e) {
        console.warn('⚠️ Could not cache nav:', e);
      }
    } else {
      console.warn(`⚠️ Nav element not found on ${targetContext} page.`);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pageContextManager = new PageContextManager();
  });
} else {
  window.pageContextManager = new PageContextManager();
}

window.PageContextManager = PageContextManager;

// Debug helper function
window.debugPageContext = function () {


  const headerSelectors = [
    'shopify-section[id*="header-group"]',
    '[id*="shopify-section-header-group"]',
    '.shopify-section[class*="header-group"]',
    'header[class*="header"]'
  ];


  headerSelectors.forEach(selector => {
    const el = document.querySelector(selector);
    if (el) {
      console.log(`  ✅ ${selector}:`, el.id || el.className);
    } else {
      console.log(`  ❌ ${selector}: not found`);
    }
  });


  document.querySelectorAll('shopify-section').forEach(el => {
    console.log(`  - ${el.id}`);
  });

  console.log('=== END DEBUG ===');
};
